import { getStoreDefinition } from '@/composables/StoreDefinition'
import { isDefined } from '@/composables/utils'
import { EndPoint } from '@/enums/EndPoint'
import { SqlComparison } from '@/enums/SqlComparison'
import { SqlOrder } from '@/enums/SqlOrder'
import { TableStoreOptions } from '@/enums/TableStoreOptions'
import type { IDatasetStore } from '@/interfaces/api/IDatasetStore'
import type { IQueryOptions } from '@/interfaces/api/IQueryOptions'
import type { ITableStore } from '@/interfaces/api/ITableStore'
import type { ISimpleOption } from '@/interfaces/ISimpleOption'
import { DatasetRecord } from '@/models/DatasetRecord'
import type { User } from '@auth0/auth0-spa-js'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useSettingsStore } from '../ui/SettingsStore'
import { useAPIStore } from './ApiStore'

let _store: IDatasetStore

export const useDatasetsStore = (): ITableStore => {
  if (_store) {
    return _store
  }

  const option = TableStoreOptions.Datasets
  const useStore = defineStore(option.id, getStoreDefinition(option), {
    persist: {
      storage: localStorage,
      pick: ['itemsPerPage']
    }
  })
  _store = useStore() as unknown as IDatasetStore
  _extend(_store)
  return _store
}

const _extend = (store: IDatasetStore) => {
  const apiStore = useAPIStore()
  const settingsStore = useSettingsStore()
  store.enabledItems = ref<Array<DatasetRecord>>([]) as unknown as Array<DatasetRecord>

  store.enabledOptions = computed(() => {
    return store.enabledItems.map((item: DatasetRecord) => {
      return { title: item.name, value: item.id }
    }) as Array<ISimpleOption>
  }) as unknown as Array<ISimpleOption>

  store.activeDataset = computed(() => {
    const dataset = store.enabledItems.find((item: DatasetRecord) => {
      return item.id === settingsStore.activeDatasetId
    })
    return dataset
  }) as unknown as DatasetRecord

  store.initialize = async (auth0User: User): Promise<boolean> => {
    const settingsStore = useSettingsStore()
    let activeDataset

    // If there is an active dataset stored in the browser and it exists then use it.
    if (isDefined(settingsStore.activeDatasetId)) {
      const foundDataset = store.enabledItems.find((dataset) => {
        return dataset.id === settingsStore.activeDatasetId
      })

      // Dataset found, use it.
      if (foundDataset) {
        activeDataset = foundDataset
      } else {
        // Otherwise use the default dataset if it exists.
        activeDataset = store.enabledItems[0]
        settingsStore.activeDatasetId = isDefined(activeDataset) ? activeDataset.id! : null
      }
    } else {
      // Otherwise use the default dataset if it exists.
      activeDataset = store.enabledItems[0]
      settingsStore.activeDatasetId = isDefined(activeDataset) ? activeDataset.id! : null
    }

    // If the account/dataset is initialized, no need to reinitialize it.
    if (activeDataset?.isInitialized) {
      return true
    }

    // Attempt to initialize a new account or dateset.
    const response = await apiStore.post('app/initialize', {
      firstName: auth0User.given_name ?? 'Program',
      lastName: auth0User.family_name ?? 'Administrator',
      localeCode: settingsStore.localeCode,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    })

    if (!response.error) {
      if (store.enabledItems.length === 0) {
        store.enabledItems = response.data as Array<DatasetRecord>
        settingsStore.activeDatasetId = isDefined(store.enabledItems[0])
          ? store.enabledItems[0].id!
          : null
      }
      return true
    }

    // An error occurred and we are unable to activate a dataset.
    settingsStore.activeDatasetId = null
    return false
  }

  /**
   * Loads all datasets that are enabled.
   * @returns
   */
  store.loadEnabled = async (): Promise<boolean> => {
    const query: IQueryOptions = {
      isDistinct: true,
      where: [
        {
          field: 'isEnabled',
          comparison: SqlComparison.Equal,
          value1: true
        }
      ],
      orderBy: [{ field: 'name', order: SqlOrder.ASC }]
    }
    const response = await apiStore.getMany(EndPoint.Datasets, query)
    let success = false
    if (!response.error) {
      store.enabledItems = (response.data.results as Array<DatasetRecord>) ?? []
      success = true
    }
    return success
  }
}
